import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Box, Paper, TextField, Button, Typography, Alert,
    InputAdornment, IconButton, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined, EmailOutlined } from '@mui/icons-material';

export default function Login() {
    const [email, setEmail]         = useState('');
    const [password, setPassword]   = useState('');
    const [showPass, setShowPass]   = useState(false);
    const [error, setError]         = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { login }    = useAuth();
    const navigate     = useNavigate();
    const location     = useLocation();
    const from         = location.state?.from?.pathname || '/';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            setError('Please enter both email and password');
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            await login(email.trim(), password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(err.message || 'Login failed. Please check your credentials.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
        }}>
            <Paper
                elevation={3}
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    p: { xs: 3, sm: 5 },
                    borderRadius: 4,
                }}
            >
                {/* Logo & Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box sx={{
                        width: 56, height: 56,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                    }}>
                        <svg width="28" height="28" viewBox="0 0 60 60" fill="none">
                            <path d="M30 8L50 28V52H10V28L30 8Z" fill="white" />
                            <path d="M30 16L42 28V44H18V28L30 16Z" fill="rgba(255,255,255,0.3)" />
                        </svg>
                    </Box>

                    <Typography variant="h2" sx={{ mb: 0.5 }}>
                        ZycoTM
                    </Typography>
                    <Typography variant="subtitle1">
                        Sign in to your dashboard
                    </Typography>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert
                        severity="error"
                        variant="outlined"
                        sx={{ mb: 3, borderRadius: 2.5 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {/* Login Form */}
                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        autoComplete="email"
                        autoFocus
                        sx={{ mb: 2.5 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Password"
                        type={showPass ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        sx={{ mb: 3.5 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPass(!showPass)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={submitting}
                        sx={{
                            py: 1.5,
                            fontSize: '0.95rem',
                            background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #4338CA, #6D28D9)',
                            },
                        }}
                    >
                        {submitting ? (
                            <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                            'Sign In'
                        )}
                    </Button>
                </Box>

                {/* Footer */}
                <Typography
                    variant="body2"
                    sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}
                >
                    ZycoTM Dashboard v1.0
                </Typography>
            </Paper>
        </Box>
    );
}
